import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Filter, Clock, Users, Trash2, MessageCircle, Link as LinkIcon, ChevronRight, Sparkles, Upload, X, Image as ImageIcon, Film } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import MobileNav from '@/components/MobileNav';
import MentionInput from '@/components/MentionInput';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InspirationsPage = () => {
  const auth = useAuth();
  const { user } = auth;
  const [inspirations, setInspirations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedInspiration, setSelectedInspiration] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [cloudinaryConfig, setCloudinaryConfig] = useState(null);
  const [searchParams] = useSearchParams();
  const containerRef = useRef(null);
  const lastScrolledId = useRef(null);

  // Create form state
  const [newText, setNewText] = useState('');
  const [newLink, setNewLink] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadedMedia, setUploadedMedia] = useState(null);
  const [targetType, setTargetType] = useState('everyone');
  const [targetUsers, setTargetUsers] = useState('');
  const [maxClaims, setMaxClaims] = useState('');
  const [deadline, setDeadline] = useState('');

  // Conversation state
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Fetch cloudinary config for upload widget
  const fetchCloudinaryConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cloudinary/config`);
      setCloudinaryConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch cloudinary config');
    }
  }, []);

  // Load Cloudinary widget script
  useEffect(() => {
    if (window.cloudinary) return;
    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const openUploadWidget = async () => {
    if (!cloudinaryConfig || !window.cloudinary) {
      toast.error('Upload widget not ready');
      console.error('Widget not ready:', { cloudinaryConfig, cloudinary: !!window.cloudinary });
      return;
    }

    console.log('Opening inspiration upload widget with config:', cloudinaryConfig);

    const widgetConfig = {
      cloudName: cloudinaryConfig.cloud_name,
      apiKey: cloudinaryConfig.api_key,
      uploadSignature: (callback, paramsToSign) => {
        console.log('Requesting signature for inspiration upload:', paramsToSign);
        axios.post(`${API}/cloudinary/sign`, paramsToSign)
          .then(response => {
            console.log('Signature response:', response.data);
            callback(response.data);
          })
          .catch(error => {
            console.error('Failed to get signature:', error);
            toast.error('Signature failed');
            callback(null);
          });
      },
      folder: 'zombify/inspirations',
      sources: ['local', 'camera'],
      multiple: false,
      maxFileSize: 100000000,
      resourceType: 'auto',
      clientAllowedFormats: ['image', 'video'],
      showAdvancedOptions: false,
      cropping: false,
      styles: {
        palette: {
          window: '#0A0A0A',
          windowBorder: '#FFDD00',
          tabIcon: '#FFDD00',
          menuIcons: '#FFFFFF',
          textDark: '#000000',
          textLight: '#FFFFFF',
          link: '#FFDD00',
          action: '#2B5CE4',
          inactiveTabIcon: '#666666',
          error: '#E53935',
          inProgress: '#2B5CE4',
          complete: '#FFDD00',
          sourceBg: '#0A0A0A'
        }
      }
    };

    const widget = window.cloudinary.createUploadWidget(
      widgetConfig,
      (error, result) => {
        console.log('Widget callback:', { error, event: result?.event, info: result?.info });
        
        if (error) {
          console.error('Upload error:', error);
          toast.error('Upload failed: ' + (error.message || 'Unknown error'));
          return;
        }
        
        if (result && result.event === 'success') {
          const info = result.info;
          setUploadedMedia({
            url: info.secure_url,
            public_id: info.public_id,
            resource_type: info.resource_type,
            format: info.format
          });
          setNewImageUrl(info.secure_url);
          toast.success('MEDIA UPLOADED!');
        }
      }
    );
    widget.open();
  };

  const removeUploadedMedia = () => {
    setUploadedMedia(null);
    setNewImageUrl('');
  };

  const fetchInspirations = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?filter_source=${filter}` : '';
      const response = await axios.get(`${API}/inspirations${params}`);
      setInspirations(response.data);
    } catch (error) {
      toast.error('Failed to load inspirations');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  }, []);

  useEffect(() => {
    fetchInspirations();
    fetchUnreadCount();
    fetchCloudinaryConfig();
  }, [fetchInspirations, fetchUnreadCount, fetchCloudinaryConfig]);

  // Handle deep link to specific inspiration
  useEffect(() => {
    if (inspirations.length === 0 || loading) return;
    
    const targetId = searchParams.get('id');
    
    if (targetId && targetId !== lastScrolledId.current) {
      // Find the inspiration and open its conversation
      const targetInsp = inspirations.find(i => i.id === targetId);
      if (targetInsp) {
        setSelectedInspiration(targetInsp);
        lastScrolledId.current = targetId;
      }
    }
  }, [inspirations, loading, searchParams]);

  const handleCreate = async () => {
    if (!newText && !newLink && !newImageUrl && !uploadedMedia) {
      toast.error('Add text, link, or media');
      return;
    }

    try {
      let targetUserIds = [];
      if (targetType === 'specific_user' && targetUsers) {
        const usernames = targetUsers.match(/@(\w+)/g)?.map(m => m.slice(1)) || [];
        targetUserIds = usernames;
      }

      await axios.post(`${API}/inspirations`, {
        text: newText || null,
        link: newLink || null,
        image_url: newImageUrl || null,
        target_type: targetType,
        target_user_ids: targetUserIds,
        max_claims: targetType === 'claimable' ? parseInt(maxClaims) || 3 : null,
        deadline: deadline || null
      });
      toast.success('Inspiration created!');
      setShowCreate(false);
      setNewText('');
      setNewLink('');
      setNewImageUrl('');
      setUploadedMedia(null);
      setTargetType('everyone');
      setTargetUsers('');
      setMaxClaims('');
      setDeadline('');
      fetchInspirations();
    } catch (error) {
      toast.error('Failed to create inspiration');
    }
  };

  const handleClaim = async (inspirationId) => {
    try {
      const response = await axios.post(`${API}/inspirations/${inspirationId}/claim`);
      toast.success(response.data.message);
      fetchInspirations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim');
    }
  };

  const handleDelete = async (inspirationId) => {
    try {
      await axios.delete(`${API}/inspirations/${inspirationId}`);
      toast.success('Inspiration deleted');
      fetchInspirations();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openConversation = async (inspiration) => {
    setSelectedInspiration(inspiration);
    try {
      const response = await axios.get(`${API}/inspirations/${inspiration.id}`);
      setConversationMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedInspiration) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/inspirations/${selectedInspiration.id}/messages`, {
        text: newMessage
      });
      setNewMessage('');
      const response = await axios.get(`${API}/inspirations/${selectedInspiration.id}`);
      setConversationMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zom-blue flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-zom-yellow border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zom-blue text-zom-white pt-20 pb-24">
      {/* Sub-header with filter and add button */}
      <div className="fixed top-16 left-0 right-0 z-30 bg-zom-black/90 border-b-2 border-zom-yellow">
        <div className="flex items-center justify-between p-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-32 bg-zom-black border-2 border-zom-white text-zom-white font-heading text-xs">
              <Filter className="w-4 h-4 mr-1" strokeWidth={2.5} />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zom-black border-2 border-zom-white">
              <SelectItem value="all">WSZYSTKIE</SelectItem>
              <SelectItem value="writers_room">WRITERS</SelectItem>
              <SelectItem value="performers">PERFORMERS</SelectItem>
            </SelectContent>
          </Select>
          <button 
            onClick={() => setShowCreate(true)} 
            className="w-10 h-10 bg-zom-yellow text-zom-black flex items-center justify-center border-2 border-zom-black shadow-brutal hover:shadow-brutal-lg transition-shadow"
          >
            <Plus className="w-6 h-6" strokeWidth={3} />
          </button>
        </div>
      </div>

      {/* Inspirations List */}
      <div className="p-4 pt-16 space-y-4">
        {inspirations.length === 0 ? (
          <div className="text-center py-16">
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-zom-yellow/40" strokeWidth={2} />
            <p className="font-heading text-lg">BRAK INSPIRACJI</p>
            <p className="text-sm mt-2 text-zom-white/60">STWÓRZ WŁASNĄ!</p>
          </div>
        ) : (
          inspirations.map((insp) => (
            <div 
              key={insp.id} 
              className="bg-zom-black border-3 border-zom-white p-4 space-y-3 shadow-brutal"
            >
              {/* Sender */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-zom-yellow text-zom-black flex items-center justify-center text-sm font-heading border-2 border-zom-black">
                    {insp.sender_email[0].toUpperCase()}
                  </div>
                  <div>
                    <span className="font-heading text-sm">@{insp.sender_email.split('@')[0]}</span>
                    {insp.sender_roles?.includes('writer') && (
                      <span className="ml-2 text-[10px] bg-zom-blue px-2 py-0.5 font-heading border border-zom-white">WRITER</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-zom-white/60 text-xs font-heading">
                  <Clock className="w-4 h-4" strokeWidth={2.5} />
                  {formatDate(insp.created_at)}
                </div>
              </div>

              {/* Content */}
              {insp.text && (
                <p className="text-zom-white whitespace-pre-wrap font-body text-sm">{insp.text}</p>
              )}
              {insp.link && (
                <a 
                  href={insp.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-zom-yellow hover:underline font-heading text-sm"
                >
                  <LinkIcon className="w-4 h-4" strokeWidth={2.5} />
                  {insp.link.length > 40 ? insp.link.slice(0, 40) + '...' : insp.link}
                </a>
              )}
              {insp.image_url && (
                <img 
                  src={insp.image_url} 
                  alt="Inspiration" 
                  className="max-h-48 object-cover border-2 border-zom-white"
                />
              )}

              {/* Target info - show type and addresses for writers/admins */}
              <div className="flex flex-wrap items-center gap-2 text-xs font-heading border-t border-b border-zom-white/20 py-2 my-2">
                {/* Target type badge - always show for writers/admins */}
                {(user?.roles?.includes('writer') || user?.roles?.includes('admin')) && (
                  <span className={`px-2 py-0.5 border ${
                    insp.target_type === 'everyone' ? 'border-zom-yellow text-zom-yellow' :
                    insp.target_type === 'claimable' ? 'border-zom-blue text-zom-blue' :
                    insp.target_type === 'specific_user' ? 'border-zom-red text-zom-red' :
                    'border-zom-white/60 text-zom-white/60'
                  }`}>
                    {insp.target_type === 'everyone' ? 'EVERYONE' :
                     insp.target_type === 'claimable' ? 'CLAIMABLE' :
                     insp.target_type === 'specific_user' ? 'PRIVATE' :
                     insp.target_type?.toUpperCase()}
                  </span>
                )}
                
                {/* Target users for private inspirations */}
                {(user?.roles?.includes('writer') || user?.roles?.includes('admin')) && insp.target_usernames?.length > 0 && (
                  <span className="text-zom-white/80">
                    → {insp.target_usernames.map(u => `@${u}`).join(', ')}
                  </span>
                )}
                
                {/* Claimable progress */}
                {insp.target_type === 'claimable' && (
                  <div className="flex items-center gap-1 text-zom-white/60">
                    <Users className="w-4 h-4" strokeWidth={2.5} />
                    {insp.current_claims}/{insp.max_claims} CLAIMED
                  </div>
                )}
                
                {/* Deadline */}
                {insp.deadline && (
                  <div className="flex items-center gap-1 text-zom-red">
                    <Clock className="w-4 h-4" strokeWidth={2.5} />
                    DEADLINE: {formatDate(insp.deadline)}
                  </div>
                )}
                {insp.conversation_count > 0 && (
                  <div className="flex items-center gap-1">
                    <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
                    {insp.conversation_count} MSG
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-3 border-t-2 border-zom-white/20">
                {insp.target_type === 'claimable' && 
                 !insp.claimed_by?.includes(user?.id) && 
                 insp.current_claims < insp.max_claims && (
                  <button 
                    onClick={() => handleClaim(insp.id)}
                    className="px-4 py-2 bg-zom-yellow text-zom-black font-heading text-xs border-2 border-zom-black shadow-brutal-sm hover:shadow-brutal transition-shadow"
                  >
                    CLAIM
                  </button>
                )}
                <button 
                  onClick={() => openConversation(insp)}
                  className="px-4 py-2 bg-transparent text-zom-white font-heading text-xs border-2 border-zom-white hover:bg-zom-white hover:text-zom-black transition-colors"
                >
                  <MessageCircle className="w-4 h-4 inline mr-1" strokeWidth={2.5} />
                  ROZMOWA
                </button>
                {(insp.sender_id === user?.id || user?.roles?.includes('admin')) && (
                  <button 
                    onClick={() => handleDelete(insp.id)}
                    className="px-3 py-2 text-zom-red hover:bg-zom-red hover:text-zom-white border-2 border-zom-red transition-colors ml-auto"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                )}
                <ChevronRight className="w-5 h-5 text-zom-white/40 ml-auto" strokeWidth={2.5} />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Inspiration Sheet */}
      <Sheet open={showCreate} onOpenChange={setShowCreate}>
        <SheetContent side="bottom" className="bg-zom-black border-t-4 border-zom-yellow h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-zom-white font-heading flex items-center gap-2 tracking-wide">
              <Sparkles className="w-6 h-6 text-zom-yellow" strokeWidth={2.5} />
              NOWA INSPIRACJA
            </SheetTitle>
          </SheetHeader>
          
          <div className="space-y-4 mt-6">
            <div>
              <label className="text-xs text-zom-yellow font-heading mb-2 block tracking-wide">TEKST</label>
              <MentionInput
                value={newText}
                onChange={setNewText}
                placeholder="Opisz inspirację..."
                rows={4}
              />
            </div>

            <div>
              <label className="text-xs text-zom-yellow font-heading mb-2 block tracking-wide">LINK (OPCJONALNIE)</label>
              <input
                type="url"
                value={newLink}
                onChange={(e) => setNewLink(e.target.value)}
                placeholder="https://..."
                className="w-full bg-zom-black border-2 border-zom-white px-4 py-3 text-zom-white font-body"
              />
            </div>

            <div>
              <label className="text-xs text-zom-yellow font-heading mb-2 block tracking-wide">FOTO/VIDEO (OPCJONALNIE)</label>
              {uploadedMedia ? (
                <div className="relative border-2 border-zom-yellow p-2">
                  <button
                    type="button"
                    onClick={removeUploadedMedia}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-zom-red text-zom-white flex items-center justify-center border-2 border-zom-black z-10"
                  >
                    <X className="w-4 h-4" strokeWidth={3} />
                  </button>
                  {uploadedMedia.resource_type === 'video' ? (
                    <div className="flex items-center gap-2 text-zom-white">
                      <Film className="w-6 h-6 text-zom-yellow" strokeWidth={2.5} />
                      <span className="font-heading text-sm">VIDEO UPLOADED</span>
                    </div>
                  ) : (
                    <img src={uploadedMedia.url} alt="Preview" className="max-h-32 object-contain" />
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={openUploadWidget}
                  className="w-full py-3 border-2 border-dashed border-zom-white/50 text-zom-white/60 font-heading text-sm flex items-center justify-center gap-2 hover:border-zom-yellow hover:text-zom-yellow transition-colors"
                >
                  <Upload className="w-5 h-5" strokeWidth={2.5} />
                  UPLOAD FOTO/VIDEO
                </button>
              )}
            </div>

            <div>
              <label className="text-xs text-zom-yellow font-heading mb-2 block tracking-wide">TYP</label>
              <Select value={targetType} onValueChange={setTargetType}>
                <SelectTrigger className="w-full bg-zom-black border-2 border-zom-white text-zom-white font-heading">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zom-black border-2 border-zom-white">
                  <SelectItem value="everyone">DLA WSZYSTKICH</SelectItem>
                  <SelectItem value="specific_user">ADRESOWANA</SelectItem>
                  <SelectItem value="claimable">DO CLAIMOWANIA</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {targetType === 'specific_user' && (
              <div>
                <label className="text-xs text-zom-yellow font-heading mb-2 block tracking-wide">ODBIORCY</label>
                <MentionInput
                  value={targetUsers}
                  onChange={setTargetUsers}
                  placeholder="@performer @performer2..."
                  rows={2}
                />
              </div>
            )}

            {targetType === 'claimable' && (
              <div>
                <label className="text-xs text-zom-yellow font-heading mb-2 block tracking-wide">MAX CLAIMS</label>
                <input
                  type="number"
                  value={maxClaims}
                  onChange={(e) => setMaxClaims(e.target.value)}
                  placeholder="3"
                  min="1"
                  className="w-full bg-zom-black border-2 border-zom-white px-4 py-3 text-zom-white font-body"
                />
              </div>
            )}

            <div>
              <label className="text-xs text-zom-yellow font-heading mb-2 block tracking-wide">DEADLINE (OPCJONALNIE)</label>
              <input
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full bg-zom-black border-2 border-zom-white px-4 py-3 text-zom-white font-body"
              />
            </div>

            <button 
              onClick={handleCreate} 
              className="w-full py-4 bg-zom-yellow text-zom-black font-heading text-lg tracking-wide border-3 border-zom-black shadow-brutal hover:shadow-brutal-lg transition-shadow"
            >
              STWÓRZ INSPIRACJĘ
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Conversation Sheet */}
      <Sheet open={!!selectedInspiration} onOpenChange={(open) => !open && setSelectedInspiration(null)}>
        <SheetContent side="right" className="bg-zom-black border-l-4 border-zom-yellow w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="text-zom-white font-heading tracking-wide">ROZMOWA</SheetTitle>
          </SheetHeader>
          
          <div className="flex flex-col h-[calc(100vh-120px)] mt-4">
            {selectedInspiration && (
              <div className="bg-zom-blue border-2 border-zom-white p-4 mb-4">
                <div className="text-xs text-zom-yellow font-heading mb-2">OD @{selectedInspiration.sender_email?.split('@')[0]}</div>
                {selectedInspiration.text && <p className="text-zom-white font-body text-sm">{selectedInspiration.text}</p>}
                {selectedInspiration.link && (
                  <a href={selectedInspiration.link} className="text-zom-yellow text-sm font-heading">{selectedInspiration.link}</a>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto space-y-3">
              {conversationMessages.map((msg) => (
                <div 
                  key={msg.id}
                  className={`p-3 border-2 ${
                    msg.sender_id === user?.id 
                      ? 'bg-zom-yellow text-zom-black border-zom-black ml-8' 
                      : 'bg-zom-black text-zom-white border-zom-white mr-8'
                  }`}
                >
                  <div className="text-xs font-heading mb-1">
                    @{msg.sender_email?.split('@')[0]}
                    {msg.sender_roles?.includes('writer') && (
                      <span className="ml-2 bg-zom-blue text-zom-white px-1 text-[10px]">WRITER</span>
                    )}
                  </div>
                  <p className="font-body text-sm">{msg.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <MentionInput
                value={newMessage}
                onChange={setNewMessage}
                onSubmit={sendMessage}
                placeholder="Napisz wiadomość..."
                disabled={sendingMessage}
              />
              <button 
                onClick={sendMessage} 
                className="w-full mt-2 py-3 bg-zom-yellow text-zom-black font-heading tracking-wide border-2 border-zom-black shadow-brutal hover:shadow-brutal-lg transition-shadow disabled:opacity-50"
                disabled={sendingMessage || !newMessage.trim()}
              >
                WYŚLIJ
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <MobileNav unreadCount={unreadCount} />
    </div>
  );
};

export default InspirationsPage;
